import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, CalendarPlus, CalendarRange } from 'lucide-react';
import api from '../utils/api';
import { useLocale } from '../contexts/LocaleProvider';
import { LT } from '../lib/appCatalogue';
import { localizedEvent } from '../lib/newsletterLocale';
import { useAppCatalogue } from '../contexts/AppCatalogueContext';

/* The space beside the greeting · what's coming up.
 *
 * Campagnes: Mosy's marketing calendar as a six-week timeline, one bar per
 *   campaign coloured by type, today as a yellow line; a click opens the
 *   campaign in Mosy.
 * Événements: the team events HR adds in the newsletter CMS, or an empty
 *   state that tells HR where to add one. */

const L = (fr, nl, en) => ({ fr, nl, en });
const COPY = {
  campaigns: L('Campagnes', 'Campagnes', 'Campaigns'),
  events:    L('Événements', 'Evenementen', 'Events'),
  open:      L('Calendrier Mosy', 'Mosy-kalender', 'Mosy calendar'),
  now:       L('en cours', 'loopt', 'running'),
  noCamps:   L('Pas de campagne dans les six prochaines semaines.', 'Geen campagnes in de komende zes weken.', 'No campaigns in the next six weeks.'),
  noEvents:  L('Aucun événement d’équipe prévu pour l’instant.', 'Nog geen teamevenementen gepland.', 'No team events planned yet.'),
  addEvent:  L('Ajouter un événement', 'Evenement toevoegen', 'Add an event'),
  more:      L('+{n} autres', '+{n} andere', '+{n} more'),
  types: {
    promotion: L('Promo', 'Promo', 'Promo'),
    brand:     L('Marque', 'Merk', 'Brand'),
    theme:     L('Thème', 'Thema', 'Theme'),
  },
};
const tr = (o, locale, vars = {}) => {
  let s = (o && (o[locale] ?? o.fr)) || '';
  Object.entries(vars).forEach(([k, v]) => { s = s.replace(`{${k}}`, v); });
  return s;
};

// Told apart by lightness as well as hue: yellow, deep green, sage.
const TYPE_COLOR = { promotion: LT.yellow, brand: LT.greenDeep, theme: '#7C9890' };
const DAY = 86400000;
const ROWS = 3;  // as tall as the greeting beside it, no more

export default function HeroCalendar({ className = '' }) {
  const { locale } = useLocale();
  const intl = locale === 'en' ? 'en-GB' : `${locale}-BE`;
  const [cal, setCal] = useState(null);
  const [events, setEvents] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [tab, setTab] = useState(null);
  // Credit the source like the sales block credits Perfy · logo from the catalogue.
  const { apps } = useAppCatalogue();
  const mosy = (apps || []).find(a => a.slug === 'mosy');

  useEffect(() => {
    api.marketingCampaigns().then(setCal).catch(() => setCal({ campaigns: [] }));
    api.newsletterEvents({ upcoming: 1, homepage: 1 })
      .then(rows => setEvents(Array.isArray(rows) ? rows : (rows?.results || [])))
      .catch(() => setEvents([]));
    api.canEditContent().then(setCanEdit).catch(() => setCanEdit(false));
  }, []);

  // Open on campaigns when there are any, else on events.
  const active = tab || (cal?.campaigns?.length ? 'campaigns' : 'events');

  const timeline = useMemo(() => {
    if (!cal?.today) return null;
    const today = new Date(`${cal.today}T00:00:00`);
    const from = new Date(today.getTime() - 3 * DAY);
    const span = (cal.horizon_days + 3) * DAY;
    const pct = (d) => Math.max(0, Math.min(100, ((d - from) / span) * 100));
    // Week ticks on Mondays.
    const ticks = [];
    const t = new Date(from);
    while (t.getDay() !== 1) t.setDate(t.getDate() + 1);
    for (; t - from <= span; t.setDate(t.getDate() + 7)) ticks.push(new Date(t));
    const rows = (cal.campaigns || []).map(c => {
      const s = new Date(`${c.start}T00:00:00`);
      const e = new Date(new Date(`${c.end}T00:00:00`).getTime() + DAY);
      return { ...c, left: pct(s), width: Math.max(1.5, pct(e) - pct(s)), running: s <= today && e > today, s, e };
    });
    return { today: pct(today), ticks: ticks.map(d => ({ d, x: pct(d) })), rows };
  }, [cal]);

  const fmt = (d, opts = { day: 'numeric', month: 'short' }) => new Intl.DateTimeFormat(intl, opts).format(d);
  const range = (r) => (r.e - r.s <= DAY ? fmt(r.s) : `${fmt(r.s)} – ${fmt(new Date(r.e.getTime() - DAY))}`);

  if (cal === null && events === null) return null;  // quiet while loading

  return (
    <section className={`min-w-0 ${className}`} aria-label={tr(COPY.campaigns, locale)}>
      <div className="flex items-center gap-1 border-b" style={{ borderColor: 'rgba(43,65,56,0.12)' }} role="tablist">
        {[['campaigns', cal?.campaigns?.length || 0], ['events', events?.length || 0]].map(([key, n]) => (
          <button key={key} type="button" role="tab" aria-selected={active === key} onClick={() => setTab(key)}
                  className="px-2 py-1 -mb-px border-b-2 text-[11px] uppercase tracking-[0.12em] transition"
                  style={{ color: active === key ? LT.greenDeep : LT.green50, borderColor: active === key ? LT.yellow : 'transparent' }}>
            {tr(COPY[key], locale)} <span className="font-num normal-case tracking-normal" style={{ color: LT.green50 }}>{n}</span>
          </button>
        ))}
        <span className="flex-1" />
        {active === 'campaigns' && timeline?.rows.length > 0 && (
          <span className="hidden lg:inline-flex items-center gap-2 text-[10.5px] pb-1 mr-1" style={{ color: LT.green50 }}>
            {Object.entries(TYPE_COLOR).map(([k, c]) => (
              <span key={k} className="inline-flex items-center gap-1"><span className="h-1.5 w-2.5 rounded-full" style={{ background: c }} />{tr(COPY.types[k], locale)}</span>
            ))}
          </span>
        )}
        {active === 'campaigns' && cal?.calendar_url && (
          <>
            {timeline?.rows.length > ROWS && (
              <span className="text-[11px] pb-1 mr-2" style={{ color: LT.green50 }}>{tr(COPY.more, locale, { n: timeline.rows.length - ROWS })}</span>
            )}
            <a href={cal.calendar_url} target="_blank" rel="noreferrer" title={tr(COPY.open, locale)}
               className="inline-flex items-center gap-1.5 pb-1 text-[10px] uppercase tracking-[0.12em] hover:opacity-80 transition" style={{ color: LT.green50 }}>
              <span>Powered by</span>
              {mosy?.logo
                ? <img src={mosy.logo} alt="" className="h-3 w-3 object-contain shrink-0" />
                : <CalendarRange className="h-3 w-3" style={{ color: mosy?.dot || LT.green50 }} />}
              <span style={{ color: LT.greenDeep }}>Mosy</span>
              <ArrowUpRight className="h-3 w-3" />
            </a>
          </>
        )}
      </div>

      {active === 'campaigns' && (
        !timeline || timeline.rows.length === 0 ? (
          <p className="pt-3 text-[13px]" style={{ color: LT.green50 }}>{tr(COPY.noCamps, locale)}</p>
        ) : (
          <div className="pt-1">
            {/* week ticks */}
            <div className="grid" style={{ gridTemplateColumns: '190px minmax(0, 1fr)' }}>
              <span />
              <div className="relative h-3.5 text-[10px] font-num" style={{ color: LT.green50 }}>
                {timeline.ticks.map(({ d, x }) => (
                  <span key={x} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${x}%` }}>{fmt(d)}</span>
                ))}
              </div>
            </div>
            <ul className="list-none p-0 m-0">
              {timeline.rows.slice(0, ROWS).map(r => {
                const Row = r.url ? 'a' : 'div';
                return (
                  <li key={r.id}>
                    <Row {...(r.url ? { href: r.url, target: '_blank', rel: 'noreferrer' } : {})}
                         title={`${r.name} · ${range(r)}`}
                         className="grid items-center gap-2 py-px group" style={{ gridTemplateColumns: '190px minmax(0, 1fr)', color: LT.greenDeep }}>
                      <span className="text-[12px] leading-[18px] truncate group-hover:underline underline-offset-2">
                        {r.name}
                      </span>
                      <span className="relative h-2.5">
                        {/* week grid + today */}
                        {timeline.ticks.map(({ x }) => (
                          <span key={x} className="absolute top-0 bottom-0 w-px" style={{ left: `${x}%`, background: 'rgba(43,65,56,0.08)' }} />
                        ))}
                        <span className="absolute -top-1 -bottom-1 w-0.5" style={{ left: `${timeline.today}%`, background: LT.yellow }} />
                        <span className="absolute inset-y-0 rounded-full" style={{ left: `${r.left}%`, width: `${r.width}%`, background: TYPE_COLOR[r.type] || LT.greenMid }} />
                      </span>
                    </Row>
                  </li>
                );
              })}
            </ul>
          </div>
        )
      )}

      {active === 'events' && (
        events && events.length > 0 ? (
          <ul className="list-none p-0 m-0 pt-1">
            {events.slice(0, 4).map(ev => {
              const e = { ...ev, ...localizedEvent(ev, locale) };
              return (
                <li key={ev.id} className="flex items-baseline gap-3 py-1.5 border-b text-[13.5px]" style={{ borderColor: 'rgba(43,65,56,0.08)', color: LT.greenDeep }}>
                  <span className="w-[72px] shrink-0 font-num text-[12px]" style={{ color: LT.green50 }}>{e.date_label}</span>
                  <span className="min-w-0 truncate">
                    {e.url ? <a href={e.url} target="_blank" rel="noreferrer" className="hover:underline">{e.title}</a> : e.title}
                    {e.location && <span style={{ color: LT.green50 }}> · {e.location}</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="pt-3 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[13px]" style={{ color: LT.green50 }}>{tr(COPY.noEvents, locale)}</p>
            {canEdit && (
              <Link to="/admin/newsletter-events" className="inline-flex items-center gap-1.5 text-[12.5px] px-3 py-1.5 rounded-full"
                    style={{ border: '1px solid rgba(43,65,56,0.2)', color: LT.greenDeep }}>
                <CalendarPlus className="h-3.5 w-3.5" /> {tr(COPY.addEvent, locale)}
              </Link>
            )}
          </div>
        )
      )}
    </section>
  );
}
